// Frontend-local upload-status channel for the PowerSync write path.
//
// The PowerSync SDK doesn't expose per-CRUD-entry "uploaded" events
// (only `statusChanged` / `statusUpdated` on the streaming-sync
// listener), so this module fills the gap with a small module-level
// pub/sub: the connector emits an `Uploaded` event from inside
// `dispatchCrudEntry` after the backend acknowledges the PUT, and any
// UI / telemetry / state code that wants a "still uploading" → "now
// uploaded" affordance subscribes here.
//
// Why frontend-local, NOT part of @citycare/shared (Codex review of
// the first cut, 2026-05-05):
//   - The shared `CaptureEvent` union is the AUTHORITATIVE row stream
//     used for reconciliation (buildspec/capture/20-ui.md §6 +
//     agdr/capture-mutation-abstraction.md). Mixing transport-status
//     signals into it breaks adapter parity (the REST adapter has no
//     local-then-remote split and couldn't emit such an event) and
//     diverges from the documented adapter contract.
//   - The local/synced distinction was supposed to live on
//     `CaptureResultEnvelope.source` per the Phase E plan, but that
//     surface is per-dispatch (one-shot) and can't represent the
//     async transition AFTER a Promise has resolved.
//   - A separate channel scoped to the PowerSync surface keeps both
//     contracts clean: CaptureEvent = authoritative row stream;
//     uploadEvents = transport-status stream (PowerSync-only).
//
// Subscriber example:
//   const off = subscribeToUploads((event) => {
//     if (event.table === 'capture_items' && event.id === itemId) {
//       setStillUploading(false)
//     }
//   })
//   // ...later
//   off()

/**
 * Emitted when the connector successfully uploads a single CRUD entry
 * to the backend (the HTTP request succeeded with a 2xx; LWW conflict
 * reapply does NOT count — that path didn't actually upload).
 */
export interface UploadedEvent {
  readonly kind: 'uploaded'
  /** SQLite table the entry targeted (e.g. `'capture_items'`). */
  readonly table: string
  /** Row id from the CRUD entry. */
  readonly id: string
}

export type UploadEventHandler = (event: UploadedEvent) => void

/**
 * Module-level subscriber set. Lives at module scope (not on the
 * connector instance) because the connector is a singleton and any
 * subscriber should be able to opt in without a reference to it.
 */
const subscribers = new Set<UploadEventHandler>()

/**
 * Register a handler for upload events. Returns an unsubscribe
 * function. Idempotent — calling unsubscribe twice is safe.
 */
export function subscribeToUploads(handler: UploadEventHandler): () => void {
  subscribers.add(handler)
  return () => {
    subscribers.delete(handler)
  }
}

/**
 * Emit an upload event to all subscribers. Called by the connector
 * from `dispatchCrudEntry` on the success branch ONLY. Underscore
 * prefix marks it as connector-internal — UI / telemetry should
 * subscribe via `subscribeToUploads`, not emit.
 *
 * Subscriber exceptions are caught and reported to console.error
 * rather than allowed to escape. This is critical: `_emitUploaded`
 * runs AFTER the backend has acknowledged the PUT but BEFORE the
 * connector reaches `batch.complete()`. If a buggy subscriber throws,
 * the unhandled exception would bubble out of `dispatchCrudEntry`
 * → `uploadData`'s `for…of` loop bails before `batch.complete()` →
 * the SDK retries the same already-uploaded entry on the next tick,
 * triggering 409 LWW conflict churn on writes the server already
 * accepted. Codex P1 caught this on the first cut.
 *
 * Iterate a snapshot rather than the live Set so a listener that
 * unsubscribes itself or another listener mid-emit doesn't trip the
 * iterator's invariants. Same reason React's event-emitter snapshots.
 */
export function _emitUploaded(table: string, id: string): void {
  const event: UploadedEvent = { kind: 'uploaded', table, id }
  const snapshot = Array.from(subscribers)
  for (const subscriber of snapshot) {
    try {
      subscriber(event)
    } catch (err) {
      // Don't let one buggy subscriber wedge the upload pipeline.
      // Surface in dev tools so the bug is visible without breaking
      // sync. Sentry / Bugsnag wiring (if added later) belongs here
      // alongside the console.error.
      console.error(
        '[upload-events] subscriber threw — isolated so the upload pipeline does not wedge',
        err,
      )
    }
  }
}

/**
 * Test helper — clear all subscribers. Tests register listeners and
 * MUST clean up to avoid cross-test leakage. Production code should
 * use the unsubscribe function returned by `subscribeToUploads`.
 */
export function _resetUploadSubscribers(): void {
  subscribers.clear()
}

// ─── Drop channel ─────────────────────────────────────────────────────────────
//
// A SEPARATE channel from the `uploaded` one above, deliberately NOT a
// widened `UploadedEvent` union. The `uploaded` channel's contract is
// "a row actually landed on the server"; a drop is the opposite (the
// row can NEVER land and has been discarded), so folding it into the
// same handler type would force every existing upload subscriber to
// branch on a kind it never asked for — and would break the existing
// "no upload event on the not_found path" assertions, which legitimately
// mean "nothing uploaded" (and still hold — a drop is not an upload).
//
// Why this exists: when the connector permanently DROPS a queued CRUD
// entry that can never sync (its FK parent was hard-deleted server-side
// → 404 not_found, or a client UUID collided with an unrelated row →
// 409 already_exists with an existingSpaceId), the row lived ONLY on
// this device. Dropping it silently is contractual data-loss the field
// user never sees. The connector already records the payload to Sentry;
// this channel lets a UI affordance tell the surveyor their offline
// change was lost so they can re-capture it. See connector-dispatch-
// create-item.ts (not_found) and connector-dispatch-space.ts
// (id_collision).

/**
 * Emitted when the connector permanently drops a CRUD entry that can
 * never upload. The local row has already been deleted and the entry
 * acked so the queue drains; the payload has been captured to Sentry.
 */
export interface DroppedEvent {
  readonly kind: 'dropped'
  /** SQLite table the dropped entry targeted (e.g. `'capture_items'`). */
  readonly table: string
  /** Row id from the dropped CRUD entry. */
  readonly id: string
  /**
   * Why the entry was dropped.
   *   `not_found`       — the FK parent (space/session) was hard-deleted
   *                       server-side, so the change can never land.
   *   `id_collision`    — the client UUID already addressed an unrelated row
   *                       at a different scope.
   *   `superseded`      — a non-contractual reference-row drop the surveyor
   *                       need not act on. Several causes emit it: an alias edit
   *                       (FFR2) that lost a last-write-wins race to a concurrent
   *                       rename (ADR-003); a cancelled-origin or off-catalogue
   *                       room/floor create; or a room create the server
   *                       permanently rejected (#785). Unlike not_found /
   *                       id_collision this is NOT contractual data-loss — the
   *                       dropped row is recoverable reference data (a cosmetic
   *                       label, or a room/floor whose captured counts preserve
   *                       themselves via their own create-item not_found
   *                       quarantine), and the sync stream / quarantine owns the
   *                       data — so DroppedChangeToast deliberately does NOT
   *                       surface it (see that file). Emitted so connector tests
   *                       can observe the drop and telemetry can count it.
   *   `delete_reverted` — a queued room-delete couldn't apply because the
   *                       owning session/building was completed elsewhere
   *                       (failed_precondition). The room is NOT lost — it
   *                       stays on the server and re-syncs — so the surveyor's
   *                       OPTIMISTIC removal must be UNDONE (the room reappears)
   *                       and they are told the delete didn't take. Distinct
   *                       from not_found so the UI re-shows the room instead of
   *                       warning about lost counts. See connector-dispatch-
   *                       space-delete.ts + use-delete-room.ts.
   * Carried for telemetry / copy differentiation. not_found / id_collision
   * share the "this change is lost" copy; superseded is suppressed;
   * delete_reverted gets its own "delete didn't apply — room restored" copy.
   */
  readonly reason:
    | 'not_found'
    | 'id_collision'
    | 'superseded'
    | 'delete_reverted'
}

export type DropEventHandler = (event: DroppedEvent) => void

/** Module-level drop-subscriber set — mirror of `subscribers` above. */
const dropSubscribers = new Set<DropEventHandler>()

/**
 * Register a handler for drop events. Returns an unsubscribe function.
 * Idempotent — calling unsubscribe twice is safe. Mirrors
 * `subscribeToUploads`.
 */
export function subscribeToDrops(handler: DropEventHandler): () => void {
  dropSubscribers.add(handler)
  return () => {
    dropSubscribers.delete(handler)
  }
}

/**
 * Emit a drop event to all subscribers. Called by the connector from
 * `dispatchCrudEntry`'s drop-and-ack carve-outs. Underscore prefix
 * marks it connector-internal — UI subscribes via `subscribeToDrops`.
 *
 * Subscriber-exception isolation is just as critical here as in
 * `_emitUploaded`: the drop carve-outs run inside `dispatchCrudEntry`
 * BEFORE `uploadData` reaches `batch.complete()`. If a buggy subscriber
 * threw, the exception would escape `dispatchCrudEntry`, `uploadData`'s
 * loop would bail before `batch.complete()`, and the SDK would re-run
 * the WHOLE batch on the next tick — re-dropping the same row forever
 * and re-firing Sentry on every cycle. Iterate a snapshot for the same
 * self-unsubscribe-safety reason as the uploads channel.
 */
export function _emitDropped(
  table: string,
  id: string,
  reason: DroppedEvent['reason'],
): void {
  const event: DroppedEvent = { kind: 'dropped', table, id, reason }
  const snapshot = Array.from(dropSubscribers)
  for (const subscriber of snapshot) {
    try {
      subscriber(event)
    } catch (err) {
      console.error(
        '[upload-events] drop subscriber threw — isolated so the upload pipeline does not wedge',
        err,
      )
    }
  }
}

/**
 * Test helper — clear all drop subscribers. Separate from
 * `_resetUploadSubscribers` so the two channels' test isolation stays
 * independent.
 */
export function _resetDropSubscribers(): void {
  dropSubscribers.clear()
}

// ─── Quarantine channel ───────────────────────────────────────────────────────
//
// A THIRD channel, distinct from `dropped`. A drop means "the count can never
// land and has been DISCARDED"; a quarantine means "the count can never land
// AS-IS but has been PRESERVED on the device (orphaned_capture_items) pending
// recovery." The two carry opposite UI copy — "your change was lost" vs. "your
// counts are held safely; recover them" — so they must not share a handler
// type. Emitted by connector-quarantine.ts after the payload is safely copied
// into the local-only table and the un-syncable row removed to drain the queue.

/**
 * Emitted when the connector preserves an un-syncable capture_items count in
 * the device-local `orphaned_capture_items` table instead of dropping it. The
 * original synced row has been removed and the entry acked so the queue drains;
 * the count is recoverable from the quarantine table.
 */
export interface QuarantinedEvent {
  readonly kind: 'quarantined'
  /** SQLite table the quarantined entry targeted (e.g. `'capture_items'`). */
  readonly table: string
  /** Row id from the quarantined CRUD entry (also the quarantine row id). */
  readonly id: string
  /** Internal reason code for telemetry (e.g. `powersync_capture_item_patch_not_found`). */
  readonly reason: string
}

export type QuarantineEventHandler = (event: QuarantinedEvent) => void

/** Module-level quarantine-subscriber set — mirror of `dropSubscribers`. */
const quarantineSubscribers = new Set<QuarantineEventHandler>()

/**
 * Register a handler for quarantine events. Returns an unsubscribe function.
 * Idempotent. Mirrors `subscribeToDrops`.
 */
export function subscribeToQuarantines(
  handler: QuarantineEventHandler,
): () => void {
  quarantineSubscribers.add(handler)
  return () => {
    quarantineSubscribers.delete(handler)
  }
}

/**
 * Emit a quarantine event to all subscribers. Called by the connector's
 * quarantine carve-out. Underscore prefix marks it connector-internal — UI
 * subscribes via `subscribeToQuarantines`.
 *
 * Subscriber-exception isolation is as critical here as the other channels:
 * the quarantine path runs inside `dispatchCrudEntry` BEFORE `uploadData`
 * reaches `batch.complete()`. A throwing subscriber would escape the loop,
 * skip `batch.complete()`, and re-run the whole batch next tick — re-quarantining
 * forever. Iterate a snapshot for self-unsubscribe safety.
 */
export function _emitQuarantined(
  table: string,
  id: string,
  reason: string,
): void {
  const event: QuarantinedEvent = { kind: 'quarantined', table, id, reason }
  const snapshot = Array.from(quarantineSubscribers)
  for (const subscriber of snapshot) {
    try {
      subscriber(event)
    } catch (err) {
      console.error(
        '[upload-events] quarantine subscriber threw — isolated so the upload pipeline does not wedge',
        err,
      )
    }
  }
}

/**
 * Test helper — clear all quarantine subscribers. Separate channel reset so
 * test isolation stays independent of the uploaded/dropped channels.
 */
export function _resetQuarantineSubscribers(): void {
  quarantineSubscribers.clear()
}

// ─── Enqueue channel ──────────────────────────────────────────────────────────
//
// A FOURTH channel, and the only one whose source is the WRITE path, not the
// connector's upload path. The other three fire from the connector when a queued
// entry leaves ps_crud (uploaded / dropped / quarantined). This one fires when a
// capture write ENTERS ps_crud — i.e. the moment the held-work total RISES.
//
// Why it exists (issue #754, the PR #732 dead-zone residual). The held-count
// indicator re-probes on connector events, on online/visibility wakes, and —
// while `navigator.onLine === false` — on a short poll (#732 / C2). But in a
// real dead zone (hangar / basement) a phone routinely still reports
// `navigator.onLine === true`: the radio is up, packets just don't flow. After a
// synced checkpoint quiesces the poll (#732 / #7), an offline +/- tap in that
// state enqueues to ps_crud yet fires NO connector event (uploads — and their
// `_emit*` — only happen online) AND leaves `isOffline()` false, so the poll
// stays disarmed and the banner shows a stale "all saved". `navigator.onLine`
// cannot distinguish this from a synced device; the only signal that the held
// total rose is the local enqueue itself. This channel is that signal: the
// capture client emits it on every queue-generating write it makes (via the
// withEnqueueSignal db wrapper), online state irrelevant, and the indicator
// re-probes + re-arms immediately. (Writes that bypass the capture client — e.g.
// useCancelSession's post-server-delete local eviction via the raw db — do not
// emit; that path is gated on a successful networked delete, so it cannot
// reproduce the dead-zone gap. The probe still reads the authoritative total on
// the next wake.)
//
// Payload shape: a bare pulse (no `table`/`id`). The other channels carry
// table+id because their UI consumers key on them (DroppedChangeToast).
// The sole consumer here is `subscribeToHeldChange` → the held-count probe,
// which ignores the payload and re-reads the device for the authoritative total;
// and the emitter is a single generic write chokepoint (the capture client's db
// wrapper) that has no one row id to report. A zero-payload pulse is the honest
// shape — inventing a fake id would mislead.

/**
 * Emitted when the capture write path writes a row to a synced table, which
 * queues a CRUD entry in ps_crud and raises the device's held-work total. A
 * pure pulse — "a local write just landed; the held total may have risen" —
 * with no payload, because its only consumer re-reads the authoritative total.
 */
export interface EnqueuedEvent {
  readonly kind: 'enqueued'
}

export type EnqueueEventHandler = (event: EnqueuedEvent) => void

/** Module-level enqueue-subscriber set — mirror of `quarantineSubscribers`. */
const enqueueSubscribers = new Set<EnqueueEventHandler>()

/**
 * Register a handler for enqueue events. Returns an unsubscribe function.
 * Idempotent. Mirrors `subscribeToQuarantines`.
 */
export function subscribeToEnqueues(handler: EnqueueEventHandler): () => void {
  enqueueSubscribers.add(handler)
  return () => {
    enqueueSubscribers.delete(handler)
  }
}

/**
 * Emit an enqueue event to all subscribers. Called by the capture write path
 * (the PowerSync capture client's db wrapper) after a local write commits.
 * Underscore prefix marks it write-path-internal — UI subscribes via
 * `subscribeToEnqueues`.
 *
 * Subscriber-exception isolation matters here too, for a different reason than
 * the connector channels: this runs from inside `db.execute` on the capture
 * write path. A throwing subscriber must not escape and reject the surveyor's
 * capture mutation (which would surface as a failed +/- tap on a write that
 * actually succeeded locally). Catch + report; iterate a snapshot for
 * self-unsubscribe safety, same as the other channels.
 */
export function _emitEnqueued(): void {
  const event: EnqueuedEvent = { kind: 'enqueued' }
  const snapshot = Array.from(enqueueSubscribers)
  for (const subscriber of snapshot) {
    try {
      subscriber(event)
    } catch (err) {
      console.error(
        '[upload-events] enqueue subscriber threw — isolated so the capture write does not fail',
        err,
      )
    }
  }
}

/**
 * Test helper — clear all enqueue subscribers. Separate channel reset so test
 * isolation stays independent of the other three channels.
 */
export function _resetEnqueueSubscribers(): void {
  enqueueSubscribers.clear()
}

// ─── Combined held-work signal ────────────────────────────────────────────────
//
// The unsynced-capture indicator (use-unsynced-capture-count.ts) is the one
// consumer that does not care WHICH channel fired — only that the device's
// held-work TOTAL may have changed and its probe must re-run. Rather than make
// it hand-wire three subscribes + three teardowns (and silently miss a FOURTH
// held-work channel added to this file later — the count would then under-report,
// the exact wrong-signal the indicator exists to prevent), expose ONE primitive
// that fans the same handler across every channel whose firing can change the
// held total and returns a single unsubscribe (duplication / m2).
//
// A further held-work channel joins the indicator's signal by being added to the
// HELD_CHANGE_CHANNELS registry below, NOT by a remembered edit in the consumer.
// Do NOT retrofit DroppedChangeToast / use-delete-room onto this — those
// subscribe with per-channel handlers that branch on event.reason/.kind, so the
// uniform-handler shape does not fit them.
//
// The registry spans BOTH directions of held-work change: the connector channels
// (uploaded | dropped | quarantined) fire when a queued entry LEAVES ps_crud
// (the total falls); the enqueue channel fires when a capture write ENTERS
// ps_crud (the total rises). The rising edge is the one that has no other signal
// in a `navigator.onLine === true` dead zone — see the enqueue channel header
// and issue #754.

/**
 * The single source of truth for "which channels can change the held-work
 * total." `subscribeToHeldChange` fans across exactly this set, so a new
 * held-work channel is wired into the indicator by being added HERE (one edit),
 * and CANNOT be silently missed. The lint:unsynced-capture-indicator gate
 * asserts every `subscribeTo*` export in this file appears in this registry, so
 * a new channel that forgets to register reddens CI rather than under-reporting.
 */
const HELD_CHANGE_CHANNELS = [
  subscribeToEnqueues,
  subscribeToUploads,
  subscribeToDrops,
  subscribeToQuarantines,
] as const

/**
 * Register one handler for ANY held-work change (enqueued | uploaded | dropped |
 * quarantined — and any future channel added to HELD_CHANGE_CHANNELS). Returns a
 * single unsubscribe that tears down every channel. Idempotent — calling
 * unsubscribe twice is safe.
 */
export function subscribeToHeldChange(handler: () => void): () => void {
  const offs = HELD_CHANGE_CHANNELS.map((subscribe) => subscribe(() => handler()))
  return () => {
    for (const off of offs) off()
  }
}
